import React, { useEffect, useRef, useCallback, useState, Fragment } from "react";
import { inject, observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { Col, Form, Popover, Row, Tooltip } from "antd";
import { FilePdfOutlined, FileOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, DeleteFilled } from "@ant-design/icons";
import AppButton from "components/AppButton/AppButton";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import ChatAvatar from "./components/ChatAvatar";
import { getGatewayMessageBody } from "utils/inboxPreview";
import SenderMenu from "components/GatewayMessage/MessageBox/SenderMenu";
import MessageContainer from "components/GatewayMessage/MessageContainer";
import { imageSupportList, videoSupportList, audioSupportList } from "common/utils/fileSupportList";
import { dateTimeFormatWithSeconds } from "constant/constant";
import AlertModal from "components/Modals/AlertModal";
import { resolveDefaultComposeSender, getParsedContact } from "constant/messageConstants";
import { checkIsEnglish, compileMessageForLength, caculateMessageLengthWithLineBreaks, checkTotalMessageCount } from "common/utils/messageUtilities";
import styles from "./chat.module.scss";
import ViewMedia from "components/ContentDrawer/ViewMediaModal";
import MessageType from "components/GatewayMessage/MessageType";

const MessageSchema = Yup.object().shape({
  message: Yup.string().when(["media", "url"], {
    is: (media, url) => !media && !url,
    then: schema => schema.required("Enter a message before sending"),
    otherwise: schema => schema.optional().nullable()
  }),
  sender: Yup.string()
    .max(11, "no longer then 11 characters")
    .required("Select a sender")
});

function Chat(props) {
  const {
    key,
    showHeader = false,
    modalStore,
    authStore,
    contactName,
    contactNumber,
    messageList,
    currentMessageContent,
    showMessageBox,
    handleSend,
    senderId,
    chatBoxClass,
    phoneFrameImageUrl,
    messageStore,
    isMms,
    smallSize = false,
    integrationStore,
    squareSpaceBtn = false,
    onRefresh,
    isMobile,
    handleHideChat,
    onCloseChat
  } = props;

  const [closeChatConfirm, setCloseChatConfirm] = useState(false);
  const [closingChat, setClosingChat] = useState(false);

  const history = useHistory();
  const location = useLocation();
  const chatEndRefElement = useRef();
  const formikRef = useRef();
  const chatScrollRef = useRef();
  const [loading, setLoading] = useState(false);
  const [scrollHeight, setScroll] = useState([]);
  const [searchedContact, setSearchedContact] = useState();
  const [lowBalanceAlert, setLowBalanceAlert] = useState(false);
  const [isSearchingContact, setIsSearchingContact] = useState(false);
  const [viewMedia, setViewMedia] = useState(false);

  const { loadMore, canLoadMoreConversation } = messageStore;
  const { user, dedicatedNumList, allSenders } = authStore;
  const { invoiceLink, searchContact, contact, setInvoiceLink } = integrationStore;

  const getComposeSender = () => resolveDefaultComposeSender(user, allSenders, isMms);

  useEffect(() => {
    formikRef.current?.setFieldValue("message", "");
  }, []);

  useEffect(() => {
    if (isSearchingContact) return;
    setSearchedContact(contact?.email ? contact : undefined);
  }, [contact]);

  useEffect(() => {
    if (invoiceLink) {
      formikRef.current?.setFieldValue("message", formikRef.current.values?.message + "\n\n" + invoiceLink);
    }
  }, [invoiceLink]);

  useEffect(() => {
    if (!showMessageBox) return;
    formikRef.current?.resetForm();
    if (isMms) handleRemoveFile();
    formikRef.current?.setFieldValue("sender", getComposeSender());
  }, [senderId, key, user?.defaultSender]);

  useEffect(() => {
    if (!showMessageBox) return;
    if (isMms) {
      const current = formikRef.current?.values?.sender;
      if (dedicatedNumList?.mms?.includes(current)) {
        formikRef.current?.setFieldValue("sender", current);
      } else {
        formikRef.current?.setFieldValue("sender", getComposeSender());
      }
    } else {
      formikRef.current?.setFieldValue("sender", getComposeSender());
    }
  }, [isMms]);

  useEffect(() => {
    if (messageList?.length <= 20) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      setScroll([]);
    } else {
      if (scrollHeight.length == 1) {
        chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight - scrollHeight[0];
      } else {
        chatScrollRef.current.scrollTop = scrollHeight[scrollHeight.length - 1] - scrollHeight[scrollHeight.length - 2];
      }
    }
  }, [messageList, currentMessageContent]);

  useEffect(() => {
    if (!showMessageBox) return;
    if (formikRef.current?.media) {
      messageStore?.setCurrentImage(formikRef.current?.media);
    } else {
      messageStore?.setCurrentImage(null);
    }
  }, [formikRef.current?.media]);

  // The scroll listener
  const handleScroll = useCallback(() => {
    if (chatScrollRef.current.scrollTop == 0 && canLoadMoreConversation()) {
      setLoading(true);
      loadMore().finally(res => {
        setLoading(false);
      });
      setScroll(old => [...old, chatScrollRef.current.scrollHeight]);
    }
  }, []);

  // Attach the scroll listener to the div
  useEffect(() => {
    const div = chatScrollRef.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  function getStatusIcon(status) {
    let icon = "";
    switch (status) {
      case "delivered":
        icon = <i className="fas fa-check-double" />;
        break;
      case "sent":
        icon = <i className="fas fa-check" />;
        break;
      case "queued":
        icon = <i className="fas fa-user-clock" />;
        break;
      case "scheduled":
        icon = <i className="fas fa-clock" />;
        break;
      case "failed":
        icon = <i className="fa fa-times" />;
        break;
      case "blocked":
        icon = <i className="fas fa-ban" />;
        break;
      case "pending":
        icon = <i className="fas fa-clock" />;
        break;
      default:
        break;
    }
    return icon;
  }

  const initialValues = {
    message: "",
    sender: getComposeSender()
  };

  // for checking url type (blob OR File)
  const IsBlob = data => {
    if (data && typeof data === "object") {
      return URL.createObjectURL(data);
    } else {
      return data;
    }
  };

  const handleRemoveFile = () => {
    formikRef.current?.setFieldValue("media", null);
    formikRef.current?.setFieldValue("mediaName", null);
    formikRef.current?.setFieldValue("url", null);
    formikRef.current?.setFieldValue("subject", null);
    messageStore.setCurrentImage(null);
    messageStore.setPhoneSubject(null);
  };

  return (
    <Row gutter={[0, 0]} type="flex" className={`${chatBoxClass} position-relative`}>
      {showHeader && (
        <Col span={22} lg={23} className="pb-2">
          <div span={23} className="d-flex flex-row align-items-center gap-2">
            {isMobile && <i className="fas fa-arrow-left fs-5 text-info-color me-3" onClick={handleHideChat} type="button" />}
            <ChatAvatar contactNumber={contactNumber} contactName={contactName} />
            <h5 className="fw-900 m-0">{contactName || contactNumber}</h5>
            {squareSpaceBtn && (
              <AppButton
                light
                size="small"
                className="rouned-xl"
                disabled={isSearchingContact}
                label={
                  <Popover placement="top" autoAdjustOverflow trigger={["hover", "click"]} content={<span className="text-black">Search Contact</span>}>
                    <span>Search {isSearchingContact ? <LoadingOutlined /> : <i className="fas fa-info-circle align-middle" />}</span>
                  </Popover>
                }
                onClick={() => {
                  setIsSearchingContact(true);
                  setSearchedContact();
                  searchContact(getParsedContact(contactNumber))
                    .then(({ data }) => {
                      if (data?.email) {
                        setSearchedContact(data);
                      } else {
                        setSearchedContact({ error: "Contact Not Found" });
                      }
                    })
                    .finally(() => {
                      setIsSearchingContact(false);
                    });
                }}
              />
            )}
            {onRefresh && (
              <div className="text-center mx-3">
                <Tooltip title="Refresh">
                  <i className="fas fa-sync-alt text-info-color fs-5" type="button" onClick={onRefresh} />
                </Tooltip>
              </div>
            )}
            {onCloseChat && (
              <div className="text-center ms-auto">
                <Tooltip title="Close chat">
                  <i className="fas fa-times-circle text-danger fs-5" type="button" onClick={() => setCloseChatConfirm(true)} />
                </Tooltip>
              </div>
            )}
          </div>
          {searchedContact && (
            <>
              <hr />
              <div className="p-2 text-muted fs-5 d-flex align-items-center">
                {searchedContact?.email ? (
                  <>
                    <CheckCircleOutlined className="text-success me-2" />{" "}
                    <span>
                      Contact Found <strong>{searchedContact?.email}</strong>
                    </span>
                  </>
                ) : (
                  <>
                    <CloseCircleOutlined className="text-danger me-2" /> <span>{searchedContact?.error}</span>
                  </>
                )}
              </div>
            </>
          )}
        </Col>
      )}
      {loading && (
        <div
          className="position-absolute start-50 translate-middle text-center bg-white shadow rounded-circle"
          style={{
            top: "72px",
            width: "28px",
            height: "28px"
          }}
        >
          <LoadingOutlined className="text-info-color" />
        </div>
      )}
      <Col span={24} className="flex-grow-1">
        <div className={`${styles.chatBox} ${showHeader ? styles.inbox : ""} pe-1`} ref={chatScrollRef}>
          {messageList &&
            messageList.map((msg, i) => {
              return (
                <div className={styles.msgText} key={msg?._id || msg?.message_id || i}>
                  <div className={msg.status === "received" ? styles.clientTextCover : styles.userTextCover} style={{ maxWidth: "420px" }}>
                    {/* for mms media */}
                    {msg?.isMMS &&
                      msg?.media &&
                      msg?.media.map((mediaItem, idx) => {
                        const mediaType = mediaItem?.filetype || `image/${mediaItem?.url?.split(".").pop()}`;
                        const mediaUrl = mediaItem?.url;
                        return (
                          <div className={`${styles.mmsImage} justify-content-center`} key={mediaItem?._id || `${msg?._id}-media-${idx}`}>
                            <a href={mediaUrl} target="_blank">
                              {imageSupportList.includes(mediaType) ? (
                                <img
                                  style={{
                                    width: "-webkit-fill-available",
                                    maxWidth: isMobile ? "205px" : "400px",
                                    objectFit: "contain"
                                  }}
                                  src={mediaUrl}
                                  alt={"media"}
                                />
                              ) : videoSupportList.includes(mediaType) ? (
                                <video
                                  style={{
                                    width: "-webkit-fill-available",
                                    maxWidth: isMobile ? "205px" : "400px",
                                    objectFit: "contain"
                                  }}
                                  src={mediaUrl}
                                  controls
                                />
                              ) : audioSupportList.includes(mediaType) ? (
                                <audio style={{ maxWidth: "180px" }} controls src={mediaUrl} />
                              ) : ["txt", "xml", "smil"].includes(mediaUrl?.split(".").pop()) ? null : (
                                <div
                                  style={{
                                    maxWidth: "180px",
                                    background: "#149cbe30"
                                  }}
                                  className="rounded p-4"
                                >
                                  <FilePdfOutlined className="fs-1 text-info-color" />
                                </div>
                              )}
                            </a>
                          </div>
                        );
                      })}
                    {/* message text */}
                    {getGatewayMessageBody(msg) ? <p className="pe-4 words-dont-break-out">{getGatewayMessageBody(msg)}</p> : null}
                    {/* status icons */}
                    <div className="text-end w-100" style={{ fontSize: "10px" }}>
                      {moment(msg.send_time).isSame(moment(), "day") ? moment(msg.send_time).format(dateTimeFormatWithSeconds) : moment(msg.send_time).format(dateTimeFormatWithSeconds)}{" "}
                      {getStatusIcon(msg.status)}
                    </div>
                  </div>
                </div>
              );
            })}
          <div ref={chatEndRefElement}></div>
          {isMms && messageStore.phoneFrameSubject && (
            <div className={styles.msgText}>
              <div className={styles.userTextCover}>
                <p className="pe-4 words-dont-break-out">{`<Subject: ${messageStore.phoneFrameSubject}>`}</p>
              </div>
            </div>
          )}

          {isMms && phoneFrameImageUrl ? (
            <div className={styles.mmsImage}>
              {squareSpaceBtn && <DeleteFilled className="text-danger me-2" onClick={handleRemoveFile} />}
              {imageSupportList.includes(messageStore.mediaType) ? (
                <img
                  style={{ maxWidth: "180px" }}
                  // src={URL.createObjectURL(phoneFrameImageUrl)}
                  src={IsBlob(phoneFrameImageUrl)}
                  alt={"media"}
                />
              ) : videoSupportList.includes(messageStore.mediaType) ? (
                <video
                  style={{ maxWidth: "180px" }}
                  // src={URL.createObjectURL(phoneFrameImageUrl)}
                  src={IsBlob(phoneFrameImageUrl)}
                  controls
                />
              ) : audioSupportList.includes(messageStore.mediaType) ? (
                <div className="text-end">
                  <i class="far fa-file-audio fs-1 text-info-color p-4 rounded-3 mb-2" style={{ background: "#f7f7f7" }}></i>

                  <audio
                    style={{ maxWidth: "180px" }}
                    controls
                    // src={URL.createObjectURL(phoneFrameImageUrl)}
                    src={IsBlob(phoneFrameImageUrl)}
                  />
                </div>
              ) : (
                <div style={{ maxWidth: "180px", background: "#149cbe30" }} className="rounded p-4">
                  <FilePdfOutlined className="fs-1 text-info-color" />
                </div>
              )}
            </div>
          ) : (
            messageStore.phoneFrameImageFileUrl && (
              <div className={styles.mmsImage}>
                {squareSpaceBtn && <DeleteFilled className="text-danger me-2" onClick={handleRemoveFile} />}
                <div style={{ maxWidth: "180px", background: "#149cbe30" }} className="rounded p-4" onClick={() => setViewMedia(true)} type="button">
                  <FileOutlined className="fs-1 text-info-color" />
                </div>
              </div>
            )
          )}
          {messageStore.phoneFrameCurrentMessage && (
            <div className={styles.msgText}>
              <div className={styles.userTextCover}>
                <p className="pe-4 words-dont-break-out">{messageStore.phoneFrameCurrentMessage}</p>
              </div>
            </div>
          )}
        </div>
      </Col>
      {showMessageBox && (
        <Col span={24}>
          <Formik
            initialValues={initialValues}
            validationSchema={MessageSchema}
            innerRef={formikRef}
            onSubmit={(values, { setSubmitting, resetForm }) => {
              let tempMessage = compileMessageForLength({
                message: values?.message,
                replyLink: values?.replyLink
              });
              let totalCharacterCount = caculateMessageLengthWithLineBreaks(tempMessage);
              let totalMessagesCount = checkTotalMessageCount(totalCharacterCount, checkIsEnglish(values?.message));
              if (totalMessagesCount > authStore?.balanceObj?.maxPossibleCredits.sms) {
                setLowBalanceAlert(true);
                setSubmitting(false);
                return;
              }
              handleSend(values).finally(() => {
                setSubmitting(false);
                setInvoiceLink();
                resetForm();
              });
            }}
          >
            {({ values, errors, touched, handleSubmit, handleChange, setFieldValue }) => {
              return (
                <Form onSubmit={handleSubmit} className="mt-auto px-3 border-top rounded-bottom">
                  <Row type="flex" align="middle" justify="start" className="py-2" gutter={[10, 10]}>
                    {squareSpaceBtn && (
                      <Col span={24}>
                        <MessageType />
                      </Col>
                    )}
                    <Col span={24}>
                      <SenderMenu secondary isMms={isMms} errors={errors} touched={touched} values={values} isQuick={true} label={false} Link={false} size="default" setFieldValue={setFieldValue} />
                    </Col>
                    <MessageContainer
                      size="small"
                      label={false}
                      retailChatInvoice={searchedContact?.email}
                      formikRef={formikRef}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      errors={errors}
                      touched={touched}
                      values={values}
                      testSMS={!user.quickApproved}
                      name={"message"}
                      scheduler={!user.quickApproved}
                      quickMessage
                      isMms={isMms}
                      subject={false}
                      smallSize={smallSize}
                      minRows={2}
                      handleSubmit={handleSubmit}
                      skipCrop
                      clipboardPaste
                      endProp={<AppButton label="Send" type="submit" className="rouned-xl" light size="default" />}
                    />
                  </Row>
                  <AlertModal
                    visible={lowBalanceAlert}
                    title="Low Balance"
                    description="You don't have enough credits to send this message. Please add credits to continue."
                    onCancel={() => setLowBalanceAlert(false)}
                    primaryBtnProps={{
                      label: "Add Credits",
                      onClick: () => {
                        setLowBalanceAlert(false);
                        modalStore.toggleModal("showQuickSMSModal", false);
                        history.push("/buy-credits", {
                          from: location.pathname
                        });
                      }
                    }}
                    secondaryBtnProps={{
                      label: "Cancel",
                      onClick: () => setLowBalanceAlert(false)
                    }}
                    secondaryBtn
                    closable
                    error
                  />
                </Form>
              );
            }}
          </Formik>
        </Col>
      )}
      <ViewMedia isVisible={viewMedia} handleCancel={() => setViewMedia(false)} fileurl={messageStore.phoneFrameImageFileUrl} getUploadedDocument={authStore.getUserDocument} />
      <AlertModal
        visible={closeChatConfirm}
        title="Close chat"
        description="This conversation will be removed from your inbox. Previous messages are kept and will reappear automatically the next time a message is sent or received with this number."
        warning
        loading={closingChat}
        onCancel={() => (closingChat ? null : setCloseChatConfirm(false))}
        primaryBtnProps={{
          label: "Close chat",
          onClick: async () => {
            if (!onCloseChat) return setCloseChatConfirm(false);
            try {
              setClosingChat(true);
              await onCloseChat();
              setCloseChatConfirm(false);
            } finally {
              setClosingChat(false);
            }
          }
        }}
        secondaryBtnProps={{
          label: "Cancel",
          onClick: () => setCloseChatConfirm(false)
        }}
        secondaryBtn
        closable
      />
    </Row>
  );
}

export default inject(stores => ({
  modalStore: stores.store.modalStore,
  messageStore: stores.store.messageStore,
  templateStore: stores.store.templateStore,
  authStore: stores.store.authStore,
  integrationStore: stores.store.integrationStore
}))(observer(Chat));
